/**
 * CodeLabDesigner — coach-facing designer for code_lab scenes.
 *
 * Provides editors for language, instructions, starter/solution code,
 * test cases, hints, time limits, and a preview toggle.
 */
import React, { useState, useMemo } from 'react';
import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { requiresSceneType } from '../predicates';

const LANGUAGES = [
  { id: 'python', label: 'Python' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'sql', label: 'SQL' },
];

const SANDBOX_PROVIDERS = [
  { id: 'local', label: 'Local (subprocess)', description: 'Server-side execution — supports all languages and external APIs' },
  { id: 'stackblitz', label: 'StackBlitz', description: 'Browser-based WebContainer — ideal for JavaScript/Node.js projects' },
  { id: 'jupyterlite', label: 'JupyterLite', description: 'Python notebooks in browser (Pyodide/WASM) — ideal for AI/ML exercises, no server needed' },
];

const emptyTestCase = () => ({
  name: '',
  input: '',
  expected_output: '',
  hidden: false,
  weight: 1,
  timeout_ms: 5000,
});

const emptyHint = () => ({
  text: '',
  reveal_after_attempts: 1,
});

function CodeLabDesignerComponent({ scene, onSave }) {
  const meta = scene?.domain_metadata || {};

  const [language, setLanguage] = useState(meta.language || 'python');
  const [instructionsMd, setInstructionsMd] = useState(meta.instructions_md || '');
  const [starterCode, setStarterCode] = useState(meta.starter_code || '');
  const [solutionCode, setSolutionCode] = useState(meta.solution_code || '');
  const [testCases, setTestCases] = useState(meta.test_cases || [emptyTestCase()]);
  const [hints, setHints] = useState(meta.hints || []);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(meta.time_limit_minutes || '');
  const [sandboxProvider, setSandboxProvider] = useState(meta.sandbox_provider || 'local');
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const CodeLabPlayer = useMemo(() => {
    if (!showPreview) return null;
    try {
      return require('../../components/scenes/CodeLabPlayer').default;
    } catch {
      return null;
    }
  }, [showPreview]);

  const updateTestCase = (idx, field, value) => {
    setTestCases((prev) => prev.map((tc, i) =>
      i === idx ? { ...tc, [field]: value } : tc,
    ));
  };

  const removeTestCase = (idx) => {
    setTestCases((prev) => prev.filter((_, i) => i !== idx));
  };

  const addTestCase = () => setTestCases((prev) => [...prev, emptyTestCase()]);

  const updateHint = (idx, field, value) => {
    setHints((prev) => prev.map((h, i) =>
      i === idx ? { ...h, [field]: value } : h,
    ));
  };

  const removeHint = (idx) => {
    setHints((prev) => prev.filter((_, i) => i !== idx));
  };

  const addHint = () => setHints((prev) => [...prev, emptyHint()]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        language,
        instructions_md: instructionsMd,
        starter_code: starterCode,
        solution_code: solutionCode,
        test_cases: testCases.filter((tc) => tc.name.trim()),
        hints: hints.filter((h) => h.text.trim()),
        time_limit_minutes: timeLimitMinutes ? Number(timeLimitMinutes) : null,
        sandbox_provider: sandboxProvider,
      };
      if (onSave) await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const previewScene = useMemo(() => ({
    scene_id: scene?.scene_id || 'preview',
    domain_metadata: {
      language,
      instructions_md: instructionsMd,
      starter_code: starterCode,
      test_cases: testCases,
      hints,
      time_limit_minutes: timeLimitMinutes ? Number(timeLimitMinutes) : null,
      sandbox_provider: sandboxProvider,
    },
  }), [language, instructionsMd, starterCode, testCases, hints, timeLimitMinutes, sandboxProvider, scene]);

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <span style={{ fontSize: 20 }}>{'\u2699\uFE0F'}</span>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Code Lab Designer</h3>
      </div>

      <div style={bodyStyle}>
        {/* Language selector */}
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Language</label>
          <select value={language} onChange={(e) => setLanguage(e.target.value)} style={selectStyle}>
            {LANGUAGES.map((l) => (
              <option key={l.id} value={l.id}>{l.label}</option>
            ))}
          </select>
        </div>

        {/* Instructions */}
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Instructions (Markdown)</label>
          <textarea
            value={instructionsMd}
            onChange={(e) => setInstructionsMd(e.target.value)}
            placeholder="Write the lab instructions in markdown..."
            rows={8}
            style={textareaStyle}
          />
        </div>

        {/* Starter code */}
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Starter Code</label>
          <textarea
            value={starterCode}
            onChange={(e) => setStarterCode(e.target.value)}
            placeholder={`# Starter code for ${language}...`}
            rows={8}
            style={monoTextareaStyle}
          />
        </div>

        {/* Solution code */}
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>
            Solution Code <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(hidden from learners)</span>
          </label>
          <textarea
            value={solutionCode}
            onChange={(e) => setSolutionCode(e.target.value)}
            placeholder="Reference solution..."
            rows={6}
            style={monoTextareaStyle}
          />
        </div>

        {/* Test cases */}
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Test Cases</label>
          {testCases.map((tc, idx) => (
            <div key={idx} style={testCaseCardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>Test {idx + 1}</span>
                <button onClick={() => removeTestCase(idx)} style={removeBtnStyle}>Remove</button>
              </div>
              <div style={testFieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={smallLabelStyle}>Name</label>
                  <input
                    value={tc.name}
                    onChange={(e) => updateTestCase(idx, 'name', e.target.value)}
                    placeholder="e.g. basic_addition"
                    style={inputStyle}
                  />
                </div>
                <div style={{ width: 80 }}>
                  <label style={smallLabelStyle}>Weight</label>
                  <input
                    type="number"
                    value={tc.weight}
                    onChange={(e) => updateTestCase(idx, 'weight', Number(e.target.value))}
                    min={0}
                    style={inputStyle}
                  />
                </div>
                <div style={{ width: 100 }}>
                  <label style={smallLabelStyle}>Timeout (ms)</label>
                  <input
                    type="number"
                    value={tc.timeout_ms}
                    onChange={(e) => updateTestCase(idx, 'timeout_ms', Number(e.target.value))}
                    min={100}
                    style={inputStyle}
                  />
                </div>
              </div>
              <div style={testFieldRow}>
                <div style={{ flex: 1 }}>
                  <label style={smallLabelStyle}>Input</label>
                  <textarea
                    value={tc.input}
                    onChange={(e) => updateTestCase(idx, 'input', e.target.value)}
                    rows={2}
                    style={monoSmallTextareaStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={smallLabelStyle}>Expected Output</label>
                  <textarea
                    value={tc.expected_output}
                    onChange={(e) => updateTestCase(idx, 'expected_output', e.target.value)}
                    rows={2}
                    style={monoSmallTextareaStyle}
                  />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', marginTop: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={tc.hidden || false}
                  onChange={(e) => updateTestCase(idx, 'hidden', e.target.checked)}
                />
                Hidden test (not shown to learner)
              </label>
            </div>
          ))}
          <button onClick={addTestCase} style={addBtnStyle}>+ Add Test Case</button>
        </div>

        {/* Hints */}
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Hints</label>
          {hints.map((h, idx) => (
            <div key={idx} style={hintCardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 12, color: '#92400e' }}>Hint {idx + 1}</span>
                <button onClick={() => removeHint(idx)} style={removeBtnStyle}>Remove</button>
              </div>
              <textarea
                value={h.text}
                onChange={(e) => updateHint(idx, 'text', e.target.value)}
                placeholder="Hint text..."
                rows={2}
                style={textareaStyle}
              />
              <div style={{ marginTop: 6 }}>
                <label style={smallLabelStyle}>Reveal after attempts</label>
                <input
                  type="number"
                  value={h.reveal_after_attempts || 1}
                  onChange={(e) => updateHint(idx, 'reveal_after_attempts', Number(e.target.value))}
                  min={0}
                  style={{ ...inputStyle, width: 80 }}
                />
              </div>
            </div>
          ))}
          <button onClick={addHint} style={addBtnStyle}>+ Add Hint</button>
        </div>

        {/* Time limit */}
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Time Limit (minutes)</label>
          <input
            type="number"
            value={timeLimitMinutes}
            onChange={(e) => setTimeLimitMinutes(e.target.value)}
            placeholder="No limit"
            min={1}
            style={{ ...inputStyle, width: 120 }}
          />
        </div>

        {/* Sandbox provider */}
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Sandbox Provider</label>
          <select value={sandboxProvider} onChange={(e) => setSandboxProvider(e.target.value)} style={selectStyle}>
            {SANDBOX_PROVIDERS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
          {SANDBOX_PROVIDERS.find((p) => p.id === sandboxProvider)?.description && (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              {SANDBOX_PROVIDERS.find((p) => p.id === sandboxProvider).description}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button onClick={() => setShowPreview((v) => !v)} style={previewBtnStyle}>
            {showPreview ? 'Hide Preview' : 'Preview'}
          </button>
          <button onClick={handleSave} disabled={saving} style={saveBtnStyle}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        {/* Preview */}
        {showPreview && CodeLabPlayer && (
          <div style={{ marginTop: 20, border: '2px dashed #93c5fd', borderRadius: 12, padding: 4 }}>
            <div style={{ fontSize: 11, color: '#3b82f6', fontWeight: 700, textAlign: 'center', padding: 4 }}>
              PREVIEW
            </div>
            <CodeLabPlayer scene={previewScene} domain="preview" learnerId="coach-preview" />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────

const containerStyle = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  overflow: 'hidden',
  background: '#fff',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '14px 18px',
  background: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
};

const bodyStyle = {
  padding: '18px 20px',
};

const fieldGroupStyle = {
  marginBottom: 18,
};

const labelStyle = {
  display: 'block',
  fontWeight: 700,
  fontSize: 13,
  color: '#1e293b',
  marginBottom: 6,
};

const smallLabelStyle = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: '#64748b',
  marginBottom: 3,
};

const selectStyle = {
  padding: '7px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 13,
  background: '#fff',
  color: '#1e293b',
  width: '100%',
  maxWidth: 240,
};

const inputStyle = {
  padding: '6px 8px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 13,
  color: '#1e293b',
  width: '100%',
  boxSizing: 'border-box',
};

const textareaStyle = {
  width: '100%',
  padding: 10,
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: 'inherit',
  lineHeight: 1.5,
  resize: 'vertical',
  boxSizing: 'border-box',
  color: '#1e293b',
};

const monoTextareaStyle = {
  ...textareaStyle,
  fontFamily: "'Fira Code', 'Menlo', monospace",
  fontSize: 12,
  background: '#0f172a',
  color: '#e2e8f0',
  tabSize: 4,
};

const monoSmallTextareaStyle = {
  ...monoTextareaStyle,
  fontSize: 11,
  padding: 6,
};

const testCaseCardStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: 12,
  marginBottom: 10,
  background: '#fafbfc',
};

const testFieldRow = {
  display: 'flex',
  gap: 8,
  marginBottom: 6,
};

const hintCardStyle = {
  border: '1px solid #fde68a',
  borderRadius: 8,
  padding: 10,
  marginBottom: 8,
  background: '#fffbeb',
};

const removeBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#dc2626',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  padding: 0,
};

const addBtnStyle = {
  background: '#eff6ff',
  color: '#2563eb',
  border: '1px solid #bfdbfe',
  borderRadius: 6,
  padding: '6px 14px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  marginTop: 4,
};

const previewBtnStyle = {
  background: '#f1f5f9',
  color: '#334155',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  padding: '8px 18px',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const saveBtnStyle = {
  background: '#059669',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '8px 22px',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

function CodeLabDesignerToolMount() {
  const { scene, saveScene } = useDesigner();
  if (!scene) return null;
  return (
    <CodeLabDesignerComponent
      scene={scene}
      onSave={async (payload) => {
        if (saveScene) await saveScene({ domain_metadata: payload });
      }}
    />
  );
}

export { CodeLabDesignerComponent as CodeLabDesigner };

export const codeLabDesigner = new DesignerTool({
  id:         'platform.code_lab_designer',
  label:      'Code lab designer',
  slot:       'RIGHT_INSP',
  category:   'metadata',
  applies_to: requiresSceneType('code_lab'),
  Component:  CodeLabDesignerToolMount,
  weight:     60,
  helpText:   'Author language, instructions, starter code, test cases, and hints for code lab scenes.',
});

export default codeLabDesigner;
